// Canonical client helpers for the admin refund workflow (Phase 4A).
//
// SINGLE AUTHORITY: every refund mutation goes through the `admin-refunds`
// edge function. Nothing in this file — or in any component that uses it —
// may write class_payments.refunded_amount_cents, class_payments.payment_status,
// public.refunds, or financial_ledger_entries directly. The database guard and
// the service's own authorization remain authoritative even if the UI is
// bypassed.

import { supabase } from "@/integrations/supabase/client";

export type RefundChannel = "stripe" | "manual";

export const MANUAL_METHODS = [
  { value: "zelle", label: "Zelle" },
  { value: "check", label: "Check" },
  { value: "cash", label: "Cash" },
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "credit", label: "Credit" },
  { value: "other", label: "Other" },
] as const;

export type ManualMethod = (typeof MANUAL_METHODS)[number]["value"];

export const REFUND_REASONS = [
  "Class cancelled",
  "Duplicate payment",
  "Family requested refund",
  "Enrollment change",
  "Administrative correction",
  "Other",
] as const;

/** Shape of the `dry_run` response body we actually render. */
export interface RefundPreview {
  channel: RefundChannel;
  family: { adult_name: string; email: string };
  student: string | null;
  class: { class_id: string; session_label: string | null };
  original_amount_cents: number;
  already_completed_refund_cents: number;
  open_refund_cents: number;
  requested_refund_cents: number;
  remaining_balance_after_cents: number;
  financial_period_open: boolean;
  dispute_present: boolean;
  would_execute: boolean;
  blocker: string | null;
  blocker_message: string | null;
  writes_performed: boolean;
}

export interface ServiceCall {
  status: number;
  body: Record<string, unknown>;
}

export function formatCents(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

/** Dollar text → integer cents. Returns null for anything unparseable. */
export function parseDollarsToCents(input: string): number | null {
  const trimmed = String(input ?? "").trim().replace(/^\$/, "").replace(/,/g, "");
  if (!trimmed) return null;
  if (!/^\d*(\.\d{0,2})?$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return null;
  return Math.round(value * 100);
}

export interface AmountCheck {
  ok: boolean;
  error: string | null;
}

/** Local guardrail only — the service re-validates every amount server-side. */
export function validateAmountCents(amountCents: number | null, remainingRefundableCents: number): AmountCheck {
  if (amountCents === null || !Number.isInteger(amountCents)) {
    return { ok: false, error: "Enter a refund amount in dollars." };
  }
  if (amountCents <= 0) return { ok: false, error: "Refund amount must be greater than $0.00." };
  if (amountCents > remainingRefundableCents) {
    return {
      ok: false,
      error: `Refund amount can't exceed the ${formatCents(remainingRefundableCents)} still refundable.`,
    };
  }
  return { ok: true, error: null };
}

export function remainingRefundableCents(originalCents: number, completedCents: number, openCents = 0): number {
  return Math.max(0, originalCents - completedCents - openCents);
}

export function isFullRefund(amountCents: number, remainingRefundable: number): boolean {
  return amountCents > 0 && amountCents === remainingRefundable;
}

/** "You are refunding $40.00 of $80.00 remaining." */
export function refundingSentence(amountCents: number, remainingRefundable: number): string {
  return `You are refunding ${formatCents(amountCents)} of ${formatCents(remainingRefundable)} remaining.`;
}

export function outcomeSentence(amountCents: number, remainingRefundable: number): string {
  return isFullRefund(amountCents, remainingRefundable)
    ? "This payment will become fully refunded."
    : "This student will remain enrolled after a partial refund.";
}

export function confirmSentence(
  channel: RefundChannel,
  amountCents: number,
  methodLabel: string,
): string {
  return channel === "stripe"
    ? `This will send ${formatCents(amountCents)} back to the family's card through Stripe. Money moves as soon as you continue, and this action cannot be undone here.`
    : `This does NOT send any money. It records ${formatCents(amountCents)} that you have already returned to the family by ${methodLabel} outside this system.`;
}


export interface ExecutionGate {
  allowed: boolean;
  blocker: string | null;
}

/**
 * A dry run is mandatory before execution, and the preview must match the
 * request that is about to be sent.
 */
export function canExecute(input: {
  preview: RefundPreview | null;
  amountCents: number | null;
  channel: RefundChannel;
  reason: string;
  attested: boolean;
  submitting: boolean;
}): ExecutionGate {
  const { preview, amountCents, channel, reason, attested, submitting } = input;
  if (submitting) return { allowed: false, blocker: "A refund is already being submitted." };
  if (!preview) return { allowed: false, blocker: "Run the refund preview first." };
  if (preview.channel !== channel || preview.requested_refund_cents !== amountCents) {
    return { allowed: false, blocker: "The refund details changed — run the preview again." };
  }
  if (!reason.trim()) return { allowed: false, blocker: "A refund reason is required." };
  if (!preview.would_execute) {
    return { allowed: false, blocker: blockerMessage(preview.blocker, preview.blocker_message) };
  }

  if (channel === "manual" && !attested) {
    return { allowed: false, blocker: "Confirm that the funds have actually been sent." };
  }
  return { allowed: true, blocker: null };
}

/** Resolve the readable reason string that is sent to the service. */
export function resolveReason(choice: string, customText: string): string {
  return choice === "Other" ? customText.trim() : choice;
}

export type RefundResultState = "completed" | "processing" | "failed" | "rejected";

export function classifyResult(call: ServiceCall): RefundResultState {
  if (call.status >= 400) return "rejected";
  const state = String(call.body?.state ?? "");
  if (state === "completed") return "completed";
  if (state === "failed") return "failed";
  if (state === "processing") return "processing";
  if (call.body?.duplicate_suppressed) return "processing";
  return "processing";
}

/**
 * Human-readable copy for every refund-service rejection. Raw provider /
 * database wording (amounts in cents, charge statuses, SQL text) stays in the
 * audit log — admins only ever see these sentences.
 */
export const FRIENDLY_ERRORS: Record<string, string> = {
  over_refund: "This would refund more than the family actually paid. Check the amount and any refunds already recorded.",
  payment_not_found: "That registration payment could no longer be found. Reload the roster and try again.",
  no_stripe_session: "This payment has no card charge on file, so it can't be refunded through Stripe. Record a manual refund instead.",
  charge_unresolved: "The original card charge couldn't be located in Stripe right now. Try again shortly — don't record a manual refund unless the money really was sent another way.",
  charge_not_succeeded: "The original card charge isn't in a refundable state. Nothing was refunded.",
  charge_disputed: "This charge has a dispute or chargeback open. Refunding now could return the money twice, so it's blocked.",
  currency_mismatch: "The card charge is in a different currency than the recorded payment. This needs review before refunding.",
  amount_mismatch: "The card charge doesn't match the recorded payment amount. This needs review before refunding.",
  period_closed: "The financial period covering today is closed, so refunds are blocked until it's reopened.",
  attestation_required: "Confirm that the money has already been sent before recording a manual refund.",
  invalid_method: "Choose how the money was returned before recording a manual refund.",
  invalid_amount: "Enter a refund amount greater than $0.00.",
  invalid_request: "Some required refund details are missing. Review the form and try again.",
  forbidden_fields: "This request contained values that are set by the system. Reload the page and try again.",
  not_authorized: "You don't have permission to issue refunds.",
  refund_service_not_cut_over: "The refund service isn't active yet, so no refund was made.",
  refund_not_found: "That refund record could no longer be found.",
  not_reconcilable: "Only card refunds can be rechecked against Stripe.",
  service_error: "The refund service couldn't be reached. No refund was made — check the refund history before retrying.",
};

/** Friendly text for a service error payload; never raw exception text. */
export function serviceErrorMessage(call: ServiceCall): string {
  const code = String(call.body?.error ?? "");
  if (FRIENDLY_ERRORS[code]) return FRIENDLY_ERRORS[code];
  if (call.status === 403) return FRIENDLY_ERRORS.not_authorized;
  if (call.status === 503) return FRIENDLY_ERRORS.refund_service_not_cut_over;
  return "The refund couldn't be completed. No refund was made — check the refund history before retrying.";
}

/** Friendly text for a dry-run blocker code. */
export function blockerMessage(code: string | null, fallback: string | null): string {
  if (code && FRIENDLY_ERRORS[code]) return FRIENDLY_ERRORS[code];
  return fallback ?? "This refund is blocked.";
}

/** Friendly text for a failed provider refund. */
export function failureMessage(): string {
  return "Stripe did not complete this refund. The payment was left unchanged.";
}

/** Public-facing label for a refund history row. Never leaks provider IDs. */
export function historyMethodLabel(row: { channel: string; method: string; historical_import: boolean }): string {
  if (row.historical_import) return "Historical refund record";
  if (row.channel === "stripe") return "Stripe";
  const known = MANUAL_METHODS.find((m) => m.value === row.method);
  return known ? known.label : "Manual";
}

/** Internal bookkeeping markers that must never surface in the admin UI. */
const INTERNAL_NOTE_MARKERS = /HISTORICAL BACKFILL|Phase\s*1B|Phase\s*\d|SSH-\d{4}-\d+|ledger|attester|attested_at|idempoten/i;

/**
 * Admin note for a history row, with internal shared-ledger / import
 * bookkeeping suppressed. Historical rows are labelled as pre-existing records
 * so nobody reads them as refunds created by this interface.
 */
export function historyNote(row: { admin_note: string | null; historical_import: boolean }): string | null {
  if (row.admin_note && INTERNAL_NOTE_MARKERS.test(row.admin_note)) return null;
  return row.admin_note?.trim() ? row.admin_note : null;
}

export function historyOriginLabel(row: { historical_import: boolean; requested_at: string }): string | null {
  return row.historical_import
    ? "Recorded from prior documentation — not issued through this screen."
    : null;
}


// ---------------------------------------------------------------------------
// Service transport
// ---------------------------------------------------------------------------

export interface RefundServiceRequest {
  action: "dry_run" | "stripe" | "manual" | "reconcile";
  class_payment_id?: string;
  amount_cents?: number;
  reason?: string;
  channel?: RefundChannel;
  method?: ManualMethod;
  attestation_confirmed?: boolean;
  reference_note?: string;
  refund_id?: string;
}

export async function callRefundService(body: RefundServiceRequest): Promise<ServiceCall> {
  const { data, error } = await supabase.functions.invoke("admin-refunds", { body });
  if (error) {
    const ctx = (error as unknown as { context?: Response }).context;
    if (ctx && typeof ctx.json === "function") {
      try {
        const parsed = (await ctx.json()) as Record<string, unknown>;
        return { status: ctx.status ?? 500, body: parsed };
      } catch {
        /* fall through to the generic shape */
      }
    }
    return { status: 500, body: { error: "service_error", message: error.message } };
  }
  return { status: 200, body: (data ?? {}) as Record<string, unknown> };
}
